import Vue from "vue";
import Router from "vue-router";
import { store } from "./store";
import { auth, authReadyPromise } from "@/firebase";

// import moment from 'moment'

Vue.use(Router);

const authGuard = async (to, from, next) => {
  console.log("🛡️ Auth guard triggered for route:", to.path);

  try {
    const currentUser = store.getters.currentUser;
    const isAuthenticating = store.getters.isAuthenticating;

    if (currentUser) {
      console.log(
        "🔍 Auth guard: currentUser exists in store, balance:",
        currentUser.balance
      );

      // Don't refresh user data if:
      // 1. Coming from signin/signup (to prevent navigation loops)
      // 2. Currently in authentication flow
      // 3. User data was refreshed recently (within 5 minutes)
      const fromAuthPage = from.path === "/signin" || from.path === "/signup";
      const lastRefresh = store.getters.lastUserRefresh;
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      const recentlyRefreshed = lastRefresh && lastRefresh > fiveMinutesAgo;

      if (fromAuthPage) {
        console.log("🚫 Auth guard: Skipping refresh - coming from auth page");
        return next();
      }

      if (isAuthenticating) {
        console.log(
          "🚫 Auth guard: Skipping refresh - authentication in progress"
        );
        return next();
      }

      if (recentlyRefreshed) {
        console.log("🚫 Auth guard: Skipping refresh - recently refreshed");
        return next();
      }

      // Only refresh if coming from non-auth pages and not recently refreshed
      console.log("🔄 Auth guard: Refreshing user data");
      await store.dispatch("getCurrentUser");
      const updatedUser = store.getters.currentUser;
      console.log(
        "✅ Auth guard: User data refreshed, new balance:",
        updatedUser?.balance
      );
      return next();
    } else {
      console.log("🚫 Auth guard: No currentUser, redirecting to signin");
      return next("/signin");
    }
  } catch (error) {
    console.error("❌ Auth guard error:", error);
    return next("/signin");
  }
};

const routes = [
  {
    path: "/",
    name: "Root",
    redirect: "/home"
  },
  {
    path: "/demo/:id/:token",
    name: "Demo",
    component: () => import("@/views/Demo")
  },

  {
    path: "/signin",
    name: "Signin",
    meta: {
      title: "Sign In"
    },
    component: () => import("@/components/Design/View/ViewSignIn")
  },
  {
    path: "/signup",
    name: "Signup",
    meta: {
      title: "Sign Up"
    },
    component: () => import("@/components/Design/View/ViewSignUp")
  },
  {
    path: "/home",
    name: "home",
    meta: {
      title: "Home"
    },
    component: () => import("@/views/Home"),
    beforeEnter: authGuard
  },
  {
    path: "/articles/:id",
    name: "articles",
    meta: {
      title: "Articles"
    },
    component: () => import("@/views/Articles"),
    beforeEnter: authGuard
  },
  {
    path: "/rewards/:id",
    name: "rewards",
    meta: {
      title: "Rewards"
    },
    component: () => import("@/views/Rewards"),
    beforeEnter: authGuard
  },
  {
    path: "/offers",
    name: "offers",
    component: () => import("@/views/Offers"),
    beforeEnter: authGuard
  },
  {
    path: "/quizzes",
    name: "quizes",
    component: () => import("@/views/Quizzes"),
    beforeEnter: authGuard
  },
  {
    path: "/profile",
    name: "profile",
    component: () => import("@/views/Profile"),
    beforeEnter: authGuard
  },
  {
    path: "/page/:id",
    name: "page",
    component: () => import("@/views/Page"),
    beforeEnter: authGuard
  },
  {
    path: "/template/:id",
    name: "template",
    component: () => import("@/views/Template"),
    beforeEnter: authGuard
  }
];

// Add development-only routes
if (process.env.NODE_ENV === "development") {
  routes.push({
    path: "/test-encryption",
    name: "test-encryption",
    meta: {
      title: "Test Encryption"
    },
    component: () => import("@/components/TestEncryption")
  });
}

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: routes
});

router.beforeEach((to, from, next) => {
  console.log("🧭 Router beforeEach:", {
    to: to.path,
    from: from.path,
    toName: to.name,
    fromName: from.name
  });
  try {
    const currentProgram = store.getters.currentProgram;
    const title =
      currentProgram && currentProgram.displayTitle
        ? currentProgram.displayTitle
        : "Incentable";
    document.title = title;
  } catch (error) {
    console.error("Error setting document title:", error);
    document.title = "Incentable";
  }
  return next();
});

// Bootstrap Analytics
// Set in .env
// https://github.com/MatteoGabriele/vue-analytics
if (process.env.VUE_APP_GOOGLE_ANALYTICS) {
  Vue.use(require("vue-analytics").default, {
    id: process.env.VUE_APP_GOOGLE_ANALYTICS,
    router,
    autoTracking: {
      page: process.env.NODE_ENV !== "development"
    }
  });
}

export default router;
